import { daysDiff } from "../../../../utils/helpers";
import * as translator from "../../../../utils/translator";
import {
  TEST,
  ADDRESS_VERIFY,
  ADDRESS_VERIFY_DHL,
  ADDRESS_VERIFY_UPS,
  BLACKLIST,
  CANCELLED,
  CANCELLED_BY_CUSTOMER,
  CLARIFIED_BY_SUPPORT,
  COMPLETED,
  DELIVERED,
  DOCTOR_REQUESTED_CLARIFICATION,
  FORWARDED_REQUEST_TO_EXTERNAL_PHARMACY,
  NEED_CUSTOMER_ANSWER,
  PAUSED_FOR_STOCK_VERIFICATION_SWISS,
  PHARMACY_INTERNAL_ERROR,
  PHARMACY_PENDING_STOCK,
  PRESCRIPTION_REJECTED,
  READY_TO_SIGN,
  REJECTED_BY_PHARMACY,
  RETURNED_TO_PHARMACY,
  SENT_TO_CUSTOMER,
  SENT_TO_PHARMACY,
  WAIT_CHARGE_BACK,
  WAIT_CUSTOMER_PHARMACY_SELECTION,
  WAIT_MONEY_TRANSFER,
  WAIT_PAYMENT_CONFIRM,
  WAIT_PRESCRIPTION,
  WAIT_PRESCRIPTION_GENERATION,
  WAIT_REFUND,
  WAIT_REFUND_CONFIRMATION,
  WAIT_REPEAT_PRESCRIPTION,
  WAIT_SIGN_CONFIRMATION,
  PRESCRIPTION_REJECTED_BY_PHARMACY,
  PROCESSING_WAIT_SIGN_CONFIRMATION,
  WAIT_CUSTOMER_ALTERNATIVE_SELECTION,
  ALTERNATIVE_TREATMENT_OFFERED,
} from "../../../../utils/consts";

type StatusData = {
    class: string;
    title: string;
    description: string;
    showIcon: boolean;
};  

export function calculateColorAndDescription(orderData: {
  status: string;
  rejectReason?: string;
  rejectReasonCode?: string;
  last_status_update?: string;
  haveFeedback: boolean;
  hashID: string;
}, language: string, page: string): StatusData {
  const statusUpdated = orderData.last_status_update
    ? new Date(orderData.last_status_update)
    : new Date();

  let daysPassed = daysDiff(statusUpdated, new Date());

  // skip weekends
  if (statusUpdated.getDay() == 0) {
    daysPassed = daysPassed - 1;
  } else if (statusUpdated.getDay() == 6) {
    daysPassed = daysPassed - 2;
  }

  const statusData = { class: "", title: "", description: "", showIcon: false };
  statusData["title"] = orderData.rejectReason
    ? translator.translate(language, "my_account", "MyOrders/rejectReason")
    : "";
  statusData["description"] = orderData.rejectReasonCode === "customer_denied_alternative"
    ? translator.translate(language, "my_account", "MyOrders/rejectReasonDescriptions/customer_denied_alternative")
    : orderData.rejectReason
    ? orderData.rejectReason
    : "";
  statusData["showIcon"] = orderData.rejectReason ? true : false;

  switch (orderData.status) {
    case CLARIFIED_BY_SUPPORT:
      statusData["class"] = "warning";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${CLARIFIED_BY_SUPPORT}`
        );
      break;
    case FORWARDED_REQUEST_TO_EXTERNAL_PHARMACY:
      statusData["class"] = "warning";
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${FORWARDED_REQUEST_TO_EXTERNAL_PHARMACY}`
      );
      break;
    case WAIT_REFUND:
      statusData["class"] = "warning";
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${WAIT_REFUND}`
      );
      break;
    case ALTERNATIVE_TREATMENT_OFFERED:
      statusData["class"] = "warning";
      statusData["showIcon"] = true;
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${ALTERNATIVE_TREATMENT_OFFERED}`
      );
      break;
    case WAIT_SIGN_CONFIRMATION:
    case PROCESSING_WAIT_SIGN_CONFIRMATION:
    case WAIT_PRESCRIPTION:
    case WAIT_PRESCRIPTION_GENERATION:
      statusData["class"] = "warning";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${WAIT_PRESCRIPTION}`
        );
      break;
    case SENT_TO_PHARMACY:
      statusData["class"] = "warning";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${SENT_TO_PHARMACY}`
        );
      break;
    case PAUSED_FOR_STOCK_VERIFICATION_SWISS:
      statusData["class"] = "warning";
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${PAUSED_FOR_STOCK_VERIFICATION_SWISS}`
      );
      break;
    case SENT_TO_CUSTOMER:
      statusData["class"] = "success";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${SENT_TO_CUSTOMER}`
        );
      break;
    case DELIVERED:
      statusData["class"] = "success";
      if (!orderData.haveFeedback)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${DELIVERED}`
        );
      break;
    case NEED_CUSTOMER_ANSWER:
      statusData["class"] = "info";
      let description = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${NEED_CUSTOMER_ANSWER}`
      ) as string;
      description = description.replace(':hashID', orderData.hashID);
      statusData["description"] = description;
      break;
    case DOCTOR_REQUESTED_CLARIFICATION:
      statusData["class"] = "info";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${DOCTOR_REQUESTED_CLARIFICATION}`
        );
      break;
    case RETURNED_TO_PHARMACY:
      statusData["class"] = "info";
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${RETURNED_TO_PHARMACY}`
      );
      break;
    case REJECTED_BY_PHARMACY:
      statusData["class"] = "danger";
      statusData["description"] = translator.translate(
        language,
        "my_account",
        `MyOrders/details/disclaimerDescriptions/${REJECTED_BY_PHARMACY}`
      );
      break;
    case READY_TO_SIGN:
      statusData["class"] = "warning";
      if (daysPassed > 1)
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${READY_TO_SIGN}`
        );
      break;
    case WAIT_CUSTOMER_PHARMACY_SELECTION:
      statusData["class"] = "info";
      if (page === "my-orders") {
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/disclaimerDescriptions/${WAIT_CUSTOMER_PHARMACY_SELECTION}`
        );
      } else {
        statusData["description"] = translator.translate(
          language,
          "my_account",
          `MyOrders/details/disclaimerDescriptions/${WAIT_CUSTOMER_PHARMACY_SELECTION}`
        );
      }
      break;
    case WAIT_MONEY_TRANSFER:
    case WAIT_PAYMENT_CONFIRM:
    case WAIT_CHARGE_BACK:
    case WAIT_REFUND_CONFIRMATION:
    case ADDRESS_VERIFY:
    case ADDRESS_VERIFY_DHL:
    case ADDRESS_VERIFY_UPS:
    case WAIT_REPEAT_PRESCRIPTION:
    case PHARMACY_PENDING_STOCK:
    case PHARMACY_INTERNAL_ERROR:
      statusData["class"] = "warning";
      break;
    case CANCELLED:
    case BLACKLIST:
    case TEST:
    case CANCELLED_BY_CUSTOMER:
      statusData["class"] = "cancel";
      break;
    case COMPLETED:
      statusData["class"] = "success";
      break;
    case PRESCRIPTION_REJECTED:
    case PRESCRIPTION_REJECTED_BY_PHARMACY:
    case WAIT_CUSTOMER_ALTERNATIVE_SELECTION:
      statusData["class"] = "danger";
      break;
    default:
      statusData["class"] = "";
  }
  return statusData;
}
